// Reputation dynamics over time (MB.7). PURE (ENG-1).
//
// The gossip primitives (shareGossip / decayGossip, game/gossip) were built in
// Pass 11/13 but never ticked. This drives them: villagers who are near each
// other periodically SHARE gossip (a reputation spreads through a village), and
// every villager's gossip slowly DECAYS toward zero. This owns the cadence + the
// "who is close enough to meet" pairing; the host applies shareGossip/decayGossip
// to its per-villager ledgers on those schedules.

/** Game-ticks between share rounds (~1 min): nearby villagers swap reputation. */
export const GOSSIP_SHARE_INTERVAL = 1200;
/** Game-ticks between decay steps (~10 min): every ledger nudges toward zero. */
export const GOSSIP_DECAY_INTERVAL = 12000;
/** Villagers within this many blocks (horizontal) are close enough to "meet". */
export const GOSSIP_SHARE_RANGE = 10;

/** Index pairs (i<j) of villagers within `range` of each other — the meetings a
 *  share round resolves. O(n²), but villager counts are small and it runs rarely. */
export function meetingPairs(
  positions: readonly { x: number; z: number }[],
  range: number = GOSSIP_SHARE_RANGE,
): [number, number][] {
  const r2 = range * range;
  const out: [number, number][] = [];
  for (let i = 0; i < positions.length; i++) {
    for (let j = i + 1; j < positions.length; j++) {
      const dx = positions[i]!.x - positions[j]!.x;
      const dz = positions[i]!.z - positions[j]!.z;
      if (dx * dx + dz * dz <= r2) out.push([i, j]);
    }
  }
  return out;
}

/** True on the ticks a share round should run. */
export function isShareTick(tickCount: number): boolean {
  return tickCount > 0 && tickCount % GOSSIP_SHARE_INTERVAL === 0;
}

/** True on the ticks a decay step should run. */
export function isDecayTick(tickCount: number): boolean {
  return tickCount > 0 && tickCount % GOSSIP_DECAY_INTERVAL === 0;
}
